import { dirOf, type Locale, type Messages } from '@masar/i18n';
import { I18nProvider } from '@masar/i18n/react';
import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

const LANG_KEY = 'masar-lang';
const THEME_KEY = 'masar-theme';

export type Theme = 'light' | 'dark' | 'system';

interface Prefs {
  locale: Locale;
  setLocale: (l: Locale) => void;
  theme: Theme;
  setTheme: (t: Theme) => void;
}

const PrefsCtx = createContext<Prefs | null>(null);

function readLocale(): Locale {
  try {
    const q = /[?&]lang=(en|ar)/.exec(location.search)?.[1];
    const v = q ?? localStorage.getItem(LANG_KEY);
    return v === 'en' ? 'en' : 'ar';
  } catch {
    return 'ar';
  }
}

function readTheme(): Theme {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' ? v : 'system';
  } catch {
    return 'system';
  }
}

const EMPTY: Messages = {};

/**
 * Language (Arabic first, English loaded on demand) and theme, kept in
 * localStorage like the prototype ("masar-lang"), applied to <html>.
 */
export function PrefsProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(readLocale);
  const [theme, setThemeState] = useState<Theme>(readTheme);
  const [messages, setMessages] = useState<Messages | null>(locale === 'ar' ? EMPTY : null);

  useEffect(() => {
    const d = document.documentElement;
    d.lang = locale;
    d.dir = dirOf(locale);
    document.title = locale === 'en' ? 'Masar EV · Console' : 'مسار EV · لوحة العمل';
    if (locale === 'ar') {
      setMessages(EMPTY);
      return;
    }
    let live = true;
    void import('@masar/i18n/catalog/bundles').then((m) => {
      if (live) setMessages(m.consoleMessages);
    });
    return () => {
      live = false;
    };
  }, [locale]);

  useEffect(() => {
    const d = document.documentElement;
    if (theme === 'system') d.removeAttribute('data-theme');
    else d.setAttribute('data-theme', theme);
  }, [theme]);

  const setLocale = useCallback((l: Locale) => {
    try {
      localStorage.setItem(LANG_KEY, l);
    } catch {
      // ignore
    }
    setLocaleState(l);
  }, []);

  const setTheme = useCallback((t: Theme) => {
    try {
      if (t === 'system') localStorage.removeItem(THEME_KEY);
      else localStorage.setItem(THEME_KEY, t);
    } catch {
      // ignore
    }
    setThemeState(t);
  }, []);

  const prefs = useMemo(() => ({ locale, setLocale, theme, setTheme }), [locale, setLocale, theme, setTheme]);
  if (!messages) return null;
  return (
    <PrefsCtx value={prefs}>
      <I18nProvider locale={locale} messages={messages} warn={import.meta.env.DEV}>
        {children}
      </I18nProvider>
    </PrefsCtx>
  );
}

export function usePrefs(): Prefs {
  const v = use(PrefsCtx);
  if (!v) throw new Error('usePrefs outside <PrefsProvider>');
  return v;
}

/** The current locale for code outside React (the API client's Accept-Language). */
export function currentLocale(): Locale {
  return document.documentElement.lang === 'en' ? 'en' : 'ar';
}
